import { Anchor, AspectRatio, Blockquote, Image, List, Stack, Text, Title } from 'components'
import { Fragment, ReactNode } from 'react'
import { ARTICLE_IMAGE_MAX_WIDTH } from '../constants'
import type {
  PortableTextBlock,
  PortableTextImageBlock,
  PortableTextMarkDef,
  PortableTextSpan,
  PortableTextTextBlock,
  PortableTextVideoBlock,
} from '../interfaces'
import { buildSanityImageUrl } from '../services/sanityImage'

// Vendored Portable Text renderer (replaces @portabletext/react, which
// requires Node >= 20 to install). Covers the blockContent schema used by the
// Resource Center: text styles, nested lists, marks, images and video embeds.

const BODY_LINE_HEIGHT = 1.7
const VIDEO_ASPECT_RATIO = 16 / 9
const YOUTUBE_VIDEO_ID_LENGTH = 11
const TOP_LIST_LEVEL = 1

const applyMark = (mark: string, children: ReactNode, markDefs: PortableTextMarkDef[]): ReactNode => {
  switch (mark) {
    case 'strong':
      return <strong>{children}</strong>
    case 'em':
      return <em>{children}</em>
    case 'code':
      return <code>{children}</code>
    default:
      break
  }

  // Non-decorator marks are _key references into the block's markDefs.
  const markDef = markDefs.find(def => def._key === mark)
  if (markDef && markDef._type === 'link' && markDef.href) {
    // sx keeps the underline at rest (v6's `underline` is hover-only),
    // matching the prototype's underline="always".
    return (
      <Anchor href={markDef.href} target='_blank' rel='noopener noreferrer' sx={{ textDecoration: 'underline' }}>
        {children}
      </Anchor>
    )
  }

  // Unknown mark: keep the text instead of dropping it.
  return children
}

const renderSpan = (span: PortableTextSpan, markDefs: PortableTextMarkDef[]): ReactNode => {
  // A span can carry several marks at once; each one wraps the previous
  // result, so strong + link renders as <a><strong>text</strong></a>.
  return (span.marks || []).reduce<ReactNode>((node, mark) => applyMark(mark, node, markDefs), span.text)
}

const BlockChildren = ({ block }: { block: PortableTextTextBlock }) => {
  const markDefs = block.markDefs || []

  return (
    <>
      {(block.children || []).map((span, index) => (
        <Fragment key={span._key || index}>{renderSpan(span, markDefs)}</Fragment>
      ))}
    </>
  )
}

const TextBlock = ({ block }: { block: PortableTextTextBlock }) => {
  const children = <BlockChildren block={block} />

  switch (block.style) {
    case 'h1':
      return (
        <Title order={1} mt='lg'>
          {children}
        </Title>
      )
    case 'h2':
      return (
        <Title order={2} mt='lg'>
          {children}
        </Title>
      )
    case 'h3':
      return (
        <Title order={3} mt='md'>
          {children}
        </Title>
      )
    case 'h4':
      return (
        <Title order={4} mt='md'>
          {children}
        </Title>
      )
    case 'h5':
      return (
        <Title order={5} mt='md'>
          {children}
        </Title>
      )
    case 'h6':
      return (
        <Title order={6} mt='md'>
          {children}
        </Title>
      )
    case 'blockquote':
      // icon={null} drops v6's default quote icon; the left border mirrors
      // the prototype's flat blockquote look.
      return (
        <Blockquote
          my='sm'
          icon={null}
          sx={theme => ({
            borderLeft: `3px solid ${theme.colors.gray[4]}`,
            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
          })}
        >
          {children}
        </Blockquote>
      )
    default:
      return <Text style={{ lineHeight: BODY_LINE_HEIGHT }}>{children}</Text>
  }
}

interface ListEntry {
  item: PortableTextTextBlock
  nested: PortableTextTextBlock[]
}

const ListGroup = ({ items, level }: { items: PortableTextTextBlock[]; level: number }) => {
  if (items.length === 0) return null

  // Items deeper than the current level nest under the entry before them;
  // recursing on `nested` renders each deeper run as a sub-List.
  const entries: ListEntry[] = []
  items.forEach(item => {
    const itemLevel = item.level || TOP_LIST_LEVEL
    const lastEntry = entries[entries.length - 1]
    if (itemLevel > level && lastEntry) {
      lastEntry.nested.push(item)
    } else {
      entries.push({ item, nested: [] })
    }
  })

  const ordered = items[0].listItem === 'number'

  return (
    <List type={ordered ? 'ordered' : 'unordered'} withPadding spacing='xs'>
      {entries.map(({ item, nested }) => (
        <List.Item key={item._key}>
          <BlockChildren block={item} />
          {nested.length > 0 ? <ListGroup items={nested} level={level + 1} /> : null}
        </List.Item>
      ))}
    </List>
  )
}

// Margin-less <figure> with an optional centered caption, shared by image
// and video blocks.
const Figure = ({ caption, children }: { caption?: string; children: ReactNode }) => (
  <figure style={{ margin: 0 }}>
    {children}
    {caption ? (
      <Text component='figcaption' color='dimmed' size='sm' align='center' mt='xs'>
        {caption}
      </Text>
    ) : null}
  </figure>
)

const SanityImageFigure = ({ block }: { block: PortableTextImageBlock }) => {
  const url = buildSanityImageUrl(block, { width: ARTICLE_IMAGE_MAX_WIDTH, fit: 'max', auto: 'format' })
  if (!url) return null

  return (
    <Figure caption={block.caption}>
      <Image src={url} alt={block.alt || ''} radius='md' />
    </Figure>
  )
}

// Converts a pasted YouTube/Vimeo URL into its embeddable form.
const toEmbedUrl = (url: string | undefined): string | null => {
  if (!url) return null

  const youtubeMatch = url.match(
    new RegExp(
      `(?:youtube\\.com/(?:watch\\?v=|embed/|shorts/|live/)|youtu\\.be/)([\\w-]{${YOUTUBE_VIDEO_ID_LENGTH}})`,
      'i',
    ),
  )
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`
  }

  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  }

  return null
}

const VideoEmbedFigure = ({ block }: { block: PortableTextVideoBlock }) => {
  const embedUrl = toEmbedUrl(block.url)
  if (!embedUrl) return null

  return (
    <Figure caption={block.caption}>
      <AspectRatio ratio={VIDEO_ASPECT_RATIO} sx={theme => ({ borderRadius: theme.radius.md, overflow: 'hidden' })}>
        <iframe
          src={embedUrl}
          title={block.caption || 'Video'}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
          style={{ border: 0 }}
        />
      </AspectRatio>
    </Figure>
  )
}

type RenderChunk =
  | { kind: 'single'; block: PortableTextBlock }
  | { kind: 'list'; key: string; items: PortableTextTextBlock[] }

// Portable Text has no list container: consecutive blocks with `listItem`
// form one list. A level-1 item of a different list type starts a new list.
const chunkBlocks = (blocks: PortableTextBlock[]): RenderChunk[] => {
  const chunks: RenderChunk[] = []

  blocks.forEach(block => {
    if (block._type !== 'block' || !block.listItem) {
      chunks.push({ kind: 'single', block })
      return
    }

    const lastChunk = chunks[chunks.length - 1]
    const continuesList =
      lastChunk &&
      lastChunk.kind === 'list' &&
      ((block.level || TOP_LIST_LEVEL) > TOP_LIST_LEVEL || block.listItem === lastChunk.items[0].listItem)

    if (continuesList && lastChunk.kind === 'list') {
      lastChunk.items.push(block)
    } else {
      chunks.push({ kind: 'list', key: block._key, items: [block] })
    }
  })

  return chunks
}

const BlockRenderer = ({ block }: { block: PortableTextBlock }) => {
  switch (block._type) {
    case 'image':
      return <SanityImageFigure block={block} />
    case 'videoEmbed':
      return <VideoEmbedFigure block={block} />
    case 'block':
      return <TextBlock block={block} />
    default:
      return null
  }
}

interface PortableTextContentProps {
  value: PortableTextBlock[]
}

export const PortableTextContent = ({ value }: PortableTextContentProps) => {
  const chunks = chunkBlocks(value || [])

  return (
    <Stack spacing='md'>
      {chunks.map(chunk =>
        chunk.kind === 'list' ? (
          <ListGroup key={chunk.key} items={chunk.items} level={TOP_LIST_LEVEL} />
        ) : (
          <BlockRenderer key={chunk.block._key} block={chunk.block} />
        ),
      )}
    </Stack>
  )
}
